import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { articlesAPI, categoriesAPI, uploadAPI, authorsAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, ArrowLeft, Calendar, Clock, Image as ImageIcon, Search, UserPen } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { resolveImageUrl } from '../utils/imageUtils';

export const AdminArticleEditor = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    featured_image_alt: '',
    og_image: '',
    category_id: '',
    category_name: '',
    region: 'global',
    tags: [],
    is_published: false,
    is_featured: false,
    is_sponsored: false,
    reading_time: 5,
    meta_title: '',
    meta_description: '',
    deal_boxes: [],
    coupon_boxes: [],
    faqs: [],
    scheduled_for: null,
    status: 'draft',
    profile_author_id: ''
  });

  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publishMode, setPublishMode] = useState('draft'); // 'draft', 'publish', 'schedule'

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    const fetchAuthors = async () => {
      try {
        const response = await authorsAPI.getAll(true);
        setAuthors(response.data);
      } catch (error) {
        console.error('Failed to fetch authors:', error);
      }
    };
    fetchCategories();
    fetchAuthors();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      const fetchArticle = async () => {
        try {
          const response = await articlesAPI.getById(id);
          const article = response.data;
          
          // Debug: Log fetched article data
          console.log('[AdminArticleEditor] Fetched article:', {
            id: article.id,
            title: article.title,
            contentLength: article.content?.length || 0,
            contentPreview: article.content?.substring(0, 100) || 'NO CONTENT'
          });
          
          setFormData(article);
          
          // Set publish mode based on article status
          if (article.is_published) {
            setPublishMode('publish');
          } else if (article.scheduled_for) {
            setPublishMode('schedule');
          } else {
            setPublishMode('draft');
          }
        } catch (error) {
          console.error('[AdminArticleEditor] Failed to load article:', error);
          toast.error('Failed to load article');
          navigate('/admin');
        }
      };
      fetchArticle();
    }
  }, [id, isEditing, navigate]);

  const handleImageUpload = useCallback(async (file) => {
    try {
      console.log('[Upload] Starting upload for:', file.name, `(${(file.size / 1024).toFixed(0)}KB)`);
      const response = await uploadAPI.upload(file);
      const imageUrl = response.data.url;
      console.log('[Upload] Success — URL:', imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('[Upload] FAILED:', error?.response?.data || error.message);
      toast.error('Failed to upload image');
      throw error;
    }
  }, []);

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const imageUrl = await handleImageUpload(file);
      console.log('[Upload] Setting featured_image to:', imageUrl);
      // Use functional updater to avoid stale closure
      setFormData(prev => ({ ...prev, featured_image: imageUrl }));
      toast.success('Image uploaded and optimized');
    } catch (error) {
      // Error already handled in handleImageUpload
    } finally {
      setUploading(false);
    }
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const imageUrl = await handleImageUpload(file);
      console.log('[Upload] Setting og_image to:', imageUrl);
      setFormData(prev => ({ ...prev, og_image: imageUrl }));
      toast.success('OG Image uploaded');
    } catch (error) {
      // Error already handled
    } finally {
      setUploading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    const category = categories.find(c => c.id === selectedId);
    setFormData({
      ...formData,
      category_id: selectedId,
      category_name: category?.name || ''
    });
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const addDealBox = () => {
    setFormData({
      ...formData,
      deal_boxes: [...formData.deal_boxes, {
        store_name: '',
        offer_title: '',
        discount: '',
        expiry_date: '',
        button_text: 'Get Deal',
        button_link: ''
      }]
    });
  };

  const removeDealBox = (index) => {
    setFormData({
      ...formData,
      deal_boxes: formData.deal_boxes.filter((_, i) => i !== index)
    });
  };

  const updateDealBox = (index, field, value) => {
    const updated = [...formData.deal_boxes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, deal_boxes: updated });
  };

  const addCouponBox = () => {
    setFormData({
      ...formData,
      coupon_boxes: [...formData.coupon_boxes, {
        coupon_code: '',
        description: '',
        expiry: ''
      }]
    });
  };

  const removeCouponBox = (index) => {
    setFormData({
      ...formData,
      coupon_boxes: formData.coupon_boxes.filter((_, i) => i !== index)
    });
  };

  const updateCouponBox = (index, field, value) => {
    const updated = [...formData.coupon_boxes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, coupon_boxes: updated });
  };

  const addFaq = () => {
    setFormData({
      ...formData,
      faqs: [...(formData.faqs || []), { question: '', answer: '' }]
    });
  };

  const removeFaq = (index) => {
    setFormData({
      ...formData,
      faqs: (formData.faqs || []).filter((_, i) => i !== index)
    });
  };

  const updateFaq = (index, field, value) => {
    const updated = [...(formData.faqs || [])];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, faqs: updated });
  };

  const handleContentChange = (newContent) => {
    setFormData({ ...formData, content: newContent });
  };

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData({ 
      ...formData, 
      title: newTitle,
      slug: isEditing ? formData.slug : generateSlug(newTitle)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[Submit] handleSubmit called, publishMode:', publishMode, 'isEditing:', isEditing);
    setLoading(true);

    try {
      const submitData = { ...formData };
      console.log('[Submit] Form data keys:', Object.keys(submitData));
      
      // Handle publish mode
      if (publishMode === 'publish') {
        submitData.is_published = true;
        submitData.scheduled_for = null;
      } else if (publishMode === 'schedule') {
        submitData.is_published = false;
        // scheduled_for is already in formData
      } else {
        submitData.is_published = false;
        submitData.scheduled_for = null;
      }

      if (isEditing) {
        console.log('[Submit] Updating article:', id);
        await articlesAPI.update(id, submitData);
        toast.success('Article updated successfully');
      } else {
        console.log('[Submit] Creating new article');
        await articlesAPI.create(submitData);
        toast.success('Article created successfully');
      }
      navigate('/admin');
    } catch (error) {
      console.error('[Submit] ERROR:', error?.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-article-editor">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-brand-green hover:text-brand-red transition-colors mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-playfair font-bold text-brand-green">
            {isEditing ? 'Edit Article' : 'Create New Article'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="article-form">
          {/* Basic Information */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <h2 className="text-2xl font-playfair font-bold text-brand-green mb-6">Basic Information</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={handleTitleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Enter article title"
                  data-testid="title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">URL Slug *</label>
                <div className="flex items-center">
                  <span className="bg-gray-100 px-4 py-3 border-2 border-r-0 border-gray-300 rounded-l-lg text-gray-500">/article/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    data-testid="slug-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Excerpt *</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Brief summary for article previews..."
                  data-testid="excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Content *</label>
                <RichTextEditor
                  content={formData.content}
                  onChange={handleContentChange}
                  onImageUpload={handleImageUpload}
                  placeholder="Start writing your article content..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={formData.category_id}
                    onChange={handleCategoryChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    data-testid="category-select"
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Region *</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    data-testid="region-select"
                  >
                    <option value="global">🌍 Global</option>
                    <option value="my">🇲🇾 Malaysia</option>
                    <option value="sg">🇸🇬 Singapore</option>
                    <option value="ph">🇵🇭 Philippines</option>
                    <option value="id">🇮🇩 Indonesia</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-inter font-medium text-gray-700 mb-2">Reading Time (min)</label>
                  <input
                    type="number"
                    value={formData.reading_time}
                    onChange={(e) => setFormData({ ...formData, reading_time: parseInt(e.target.value) || 5 })}
                    min={1}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    data-testid="reading-time-input"
                  />
                </div>
              </div>

              {/* Author Selection */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <UserPen className="w-4 h-4 text-blue-600" />
                  Article Author (for public display)
                </label>
                <select
                  value={formData.profile_author_id || ''}
                  onChange={(e) => setFormData({ ...formData, profile_author_id: e.target.value || null })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent bg-white"
                  data-testid="author-select"
                >
                  <option value="">— Use CMS user name —</option>
                  {authors.map(author => (
                    <option key={author.id} value={author.id}>
                      {author.name} {author.bio ? `(${author.bio.substring(0, 40)}...)` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-blue-600 mt-2">
                  Select an author profile to display on the public article page. Leave empty to use your CMS username.
                </p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                  Tags <span className="text-gray-400">(for brand/event linking)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="Add tags like 'shopee', 'black-friday', 'electronics'"
                    data-testid="tag-input"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="bg-brand-green text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-green/90 transition-colors"
                    data-testid="add-tag-button"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="text-red-500 hover:text-red-700">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Flags */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-5 h-5 text-brand-red border-gray-300 rounded focus:ring-brand-red"
                    data-testid="featured-checkbox"
                  />
                  <span className="font-inter font-medium text-gray-700">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_sponsored}
                    onChange={(e) => setFormData({ ...formData, is_sponsored: e.target.checked })}
                    className="w-5 h-5 text-brand-red border-gray-300 rounded focus:ring-brand-red"
                    data-testid="sponsored-checkbox"
                  />
                  <span className="font-inter font-medium text-gray-700">Sponsored</span>
                </label>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white border-2 border-gray-200 p-6 rounded-xl shadow-sm">
            <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-2 flex items-center gap-2">
              <ImageIcon className="w-6 h-6 text-brand-red" />
              Featured Image
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              📐 Recommended: <strong>1200 × 675 px</strong> (16:9 aspect ratio) | Minimum: 800 × 450 px
            </p>
            
            <div className="space-y-4">
              {/* Image Preview with 16:9 container */}
              <div className="relative">
                {formData.featured_image ? (
                  <div className="relative group">
                    <div className="aspect-[16/9] w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                      <img 
                        src={resolveImageUrl(formData.featured_image)} 
                        alt={formData.featured_image_alt || 'Preview'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=675&fit=crop';
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                      className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      title="Remove image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-3 left-3 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                      16:9 Preview
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[16/9] w-full bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium mb-1">No featured image</p>
                    <p className="text-sm text-gray-400 mb-4">Upload an image or paste a URL</p>
                    <div className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                      Best size: 1200 × 675 px (16:9)
                    </div>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <label className="cursor-pointer inline-flex items-center justify-center gap-2 bg-brand-red text-white px-5 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" accept="image/*" onChange={handleFeaturedImageUpload} className="hidden" disabled={uploading} data-testid="image-upload-input" />
                </label>
                <span className="text-sm text-gray-400 text-center">or</span>
                <input
                  type="text"
                  value={formData.featured_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  placeholder="Paste image URL here..."
                  data-testid="featured-image-url-input"
                />
              </div>

              {/* Alt Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Alt Text <span className="text-gray-400 font-normal">(important for SEO & accessibility)</span>
                </label>
                <input
                  type="text"
                  value={formData.featured_image_alt}
                  onChange={(e) => setFormData({ ...formData, featured_image_alt: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  placeholder="Describe what's in the image for screen readers"
                  data-testid="image-alt-input"
                />
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <h2 className="text-2xl font-playfair font-bold text-brand-green mb-6 flex items-center gap-2">
              <Search className="w-6 h-6" />
              SEO Settings
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                  Meta Title <span className="text-gray-400">(50-60 characters recommended)</span>
                </label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Custom title for search engines (leave blank to use article title)"
                  data-testid="meta-title-input"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.meta_title?.length || 0}/60 characters</p>
              </div>

              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                  Meta Description <span className="text-gray-400">(150-160 characters recommended)</span>
                </label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Description shown in search results"
                  data-testid="meta-description-input"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.meta_description?.length || 0}/160 characters</p>
              </div>

              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                  Open Graph Image <span className="text-gray-400">(for social media sharing - 1200x630px recommended)</span>
                </label>
                <div className="space-y-2">
                  {formData.og_image && (
                    <img src={formData.og_image} alt="OG Preview" className="w-full max-w-md h-40 object-cover rounded-lg border-2 border-gray-200" />
                  )}
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg font-medium transition-colors">
                      <Upload className="w-4 h-4" />
                      Upload OG Image
                      <input type="file" accept="image/*" onChange={handleOgImageUpload} className="hidden" disabled={uploading} />
                    </label>
                    <input
                      type="text"
                      value={formData.og_image}
                      onChange={(e) => setFormData(prev => ({ ...prev, og_image: e.target.value }))}
                      className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="https://... (leave blank to use featured image)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Deal Boxes */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-playfair font-bold text-brand-green">Deal Boxes</h2>
              <button
                type="button"
                onClick={addDealBox}
                className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
                data-testid="add-deal-box-button"
              >
                <Plus className="w-4 h-4" />
                Add Deal
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.deal_boxes.map((deal, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg p-4 relative">
                  <button
                    type="button"
                    onClick={() => removeDealBox(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={deal.store_name}
                      onChange={(e) => updateDealBox(index, 'store_name', e.target.value)}
                      placeholder="Store Name (e.g., Shopee)"
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={deal.discount}
                      onChange={(e) => updateDealBox(index, 'discount', e.target.value)}
                      placeholder="Discount (e.g., 50% OFF)"
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={deal.offer_title}
                      onChange={(e) => updateDealBox(index, 'offer_title', e.target.value)}
                      placeholder="Offer Title"
                      className="md:col-span-2 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={deal.expiry_date}
                      onChange={(e) => updateDealBox(index, 'expiry_date', e.target.value)}
                      placeholder="Expiry Date"
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={deal.button_text}
                      onChange={(e) => updateDealBox(index, 'button_text', e.target.value)}
                      placeholder="Button Text"
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={deal.button_link}
                      onChange={(e) => updateDealBox(index, 'button_link', e.target.value)}
                      placeholder="Affiliate Link (URL)"
                      className="md:col-span-2 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
              {formData.deal_boxes.length === 0 && (
                <p className="text-gray-500 text-center py-8">No deal boxes added. Click "Add Deal" to add affiliate deals.</p>
              )}
            </div>
          </div>

          {/* Coupon Boxes */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-playfair font-bold text-brand-green">Coupon Boxes</h2>
              <button
                type="button"
                onClick={addCouponBox}
                className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
                data-testid="add-coupon-box-button"
              >
                <Plus className="w-4 h-4" />
                Add Coupon
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.coupon_boxes.map((coupon, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg p-4 relative">
                  <button
                    type="button"
                    onClick={() => removeCouponBox(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={coupon.coupon_code}
                      onChange={(e) => updateCouponBox(index, 'coupon_code', e.target.value)}
                      placeholder="Coupon Code (e.g., SAVE20)"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent font-mono text-lg"
                    />
                    <textarea
                      value={coupon.description}
                      onChange={(e) => updateCouponBox(index, 'description', e.target.value)}
                      placeholder="Coupon description"
                      rows={2}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={coupon.expiry}
                      onChange={(e) => updateCouponBox(index, 'expiry', e.target.value)}
                      placeholder="Expiry Date"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
              {formData.coupon_boxes.length === 0 && (
                <p className="text-gray-500 text-center py-8">No coupon boxes added. Click "Add Coupon" to add promo codes.</p>
              )}
            </div>
          </div>

          {/* FAQ Section (SEO Schema) */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-playfair font-bold text-brand-green">FAQ Schema</h2>
                <p className="text-sm text-gray-500 mt-1">Auto-generates JSON-LD for Google FAQ rich results</p>
              </div>
              <button
                type="button"
                onClick={addFaq}
                className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
                data-testid="add-faq-button"
              >
                <Plus className="w-4 h-4" />
                Add FAQ
              </button>
            </div>
            
            <div className="space-y-4">
              {(formData.faqs || []).map((faq, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg p-4 relative" data-testid={`faq-editor-${index}`}>
                  <button
                    type="button"
                    onClick={() => removeFaq(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    data-testid={`remove-faq-${index}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className="space-y-3 pr-8">
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) => updateFaq(index, 'question', e.target.value)}
                      placeholder="Question (e.g., What is the best budget phone in 2026?)"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent font-semibold"
                      data-testid={`faq-question-${index}`}
                    />
                    <textarea
                      value={faq.answer}
                      onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                      placeholder="Answer (concise, informative)"
                      rows={3}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      data-testid={`faq-answer-${index}`}
                    />
                  </div>
                </div>
              ))}
              {(!formData.faqs || formData.faqs.length === 0) && (
                <p className="text-gray-500 text-center py-8">No FAQs added. FAQs appear at the bottom of articles and generate Google FAQ rich snippets.</p>
              )}
            </div>
          </div>

          {/* Publishing Options */}
          <div className="bg-white border-2 border-gray-900 p-6 rounded-lg">
            <h2 className="text-2xl font-playfair font-bold text-brand-green mb-6 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Publishing
            </h2>
            
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${publishMode === 'draft' ? 'border-brand-green bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    name="publishMode"
                    value="draft"
                    checked={publishMode === 'draft'}
                    onChange={() => setPublishMode('draft')}
                    className="w-4 h-4 text-brand-green"
                  />
                  <div>
                    <div className="font-semibold">Save as Draft</div>
                    <div className="text-sm text-gray-500">Save without publishing</div>
                  </div>
                </label>

                <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${publishMode === 'publish' ? 'border-brand-red bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    name="publishMode"
                    value="publish"
                    checked={publishMode === 'publish'}
                    onChange={() => setPublishMode('publish')}
                    className="w-4 h-4 text-brand-red"
                  />
                  <div>
                    <div className="font-semibold">Publish Now</div>
                    <div className="text-sm text-gray-500">Make live immediately</div>
                  </div>
                </label>

                <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${publishMode === 'schedule' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    name="publishMode"
                    value="schedule"
                    checked={publishMode === 'schedule'}
                    onChange={() => setPublishMode('schedule')}
                    className="w-4 h-4 text-blue-500"
                  />
                  <div>
                    <div className="font-semibold flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Schedule
                    </div>
                    <div className="text-sm text-gray-500">Publish at specific time</div>
                  </div>
                </label>
              </div>

              {publishMode === 'schedule' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                    Schedule Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_for ? new Date(formData.scheduled_for).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFormData({ ...formData, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full max-w-xs px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="scheduled-for-input"
                  />
                  <p className="text-xs text-blue-600 mt-2">
                    Article will automatically publish at the scheduled time.
                  </p>
                </div>
              )}

              {formData.status === 'scheduled' && formData.scheduled_for && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-800">
                    Scheduled to publish on {new Date(formData.scheduled_for).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-4 font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-50 ${
                publishMode === 'publish' 
                  ? 'bg-brand-red text-white hover:bg-brand-red/90' 
                  : publishMode === 'schedule'
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-brand-green text-white hover:bg-brand-green/90'
              }`}
              data-testid="submit-article-button"
            >
              {loading ? 'Saving...' : (
                isEditing 
                  ? (publishMode === 'publish' ? 'Update & Publish' : publishMode === 'schedule' ? 'Update & Schedule' : 'Update Draft')
                  : (publishMode === 'publish' ? 'Publish Article' : publishMode === 'schedule' ? 'Schedule Article' : 'Save Draft')
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="border-2 border-gray-300 px-8 py-4 font-bold hover:bg-gray-100 transition-colors uppercase tracking-wide rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminArticleEditor;
